import { apiClient } from '@/modules/shared/services/apiClient';
import type { ApiResponse } from '@/modules/shared/types';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export interface RoleMenuPermissionItem {
  menuPermissionId: string;
  menuKey: string;
  canAccess: boolean;
  isActive: boolean;
}

export interface RoleMenuPermissionsDto {
  programId: string;
  roleId: string;
  roleName: string;
  roleCategory: string;
  permissions: RoleMenuPermissionItem[];
  accessibleMenuKeys: string[];
}

export interface MyProgramMenuAccessDto {
  programId: string;
  programName: string;
  userId: string;
  roleId: string;
  roleName: string;
  roleCategory: string;
  accessibleMenuKeys: string[];
  // ── Role Governance Properties ──
  /** Governance type: CONFIGURATION or OPERATIONAL */
  governanceType: string;
  /** True if this is a configuration-only role (e.g., Program Admin) */
  isConfigurationRole: boolean;
  /** True if this is an operational role (works through workspace) */
  isOperationalRole: boolean;
  /** Default landing route: "forms" for config roles, "workspace" for operational roles */
  defaultLandingRoute: string;
  dashboardKey: string;
  landingRoute: string;
  dashboardMappingEnabled: boolean;
  dashboardMappingIsFallback: boolean;
  permissionKeys: string[];
}

export interface UpdateRoleMenuPermissionsRequest {
  permissions: Array<{ menuKey: string; canAccess: boolean }>;
}

export interface InitializeRoleMenuPermissionsRequest {
  reset: boolean;
}

// ─────────────────────────────────────────────────────────────
// Authoritative list of all menu keys (mirrors backend MenuKeys)
// ─────────────────────────────────────────────────────────────

export const MENU_KEYS = {
  // Core Navigation
  PROGRAM_SELECTION: 'PROGRAM_SELECTION',
  PROGRAM_DASHBOARD: 'PROGRAM_DASHBOARD',
  MANAGEMENT_DASHBOARD: 'MANAGEMENT_DASHBOARD',
  OPERATIONAL_DASHBOARD: 'OPERATIONAL_DASHBOARD',
  FINANCE_DASHBOARD: 'FINANCE_DASHBOARD',
  
  // Entity Navigation
  HOSPITALS: 'HOSPITALS',
  HCPS: 'HCPS',
  PATIENTS: 'PATIENTS',
  PATIENT_360: 'PATIENT_360',
  /** Patient Workspace "Activity" tab — restricted to Program Admin by default. */
  PATIENT_ACTIVITY: 'PATIENT_ACTIVITY',
  
  // Configuration (Program Admin only)
  ROLES_CONFIG: 'ROLES_CONFIG',
  USERS_CONFIG: 'USERS_CONFIG',
  FORMS_CONFIG: 'FORMS_CONFIG',
  WORKFLOW_CONFIG: 'WORKFLOW_CONFIG',
  ASSISTANCE_SETTINGS: 'ASSISTANCE_SETTINGS',
  FINANCIAL_PLAN: 'FINANCIAL_PLAN',
  NOTIFICATIONS_CONFIG: 'NOTIFICATIONS_CONFIG',
  AUTOMATION_RULES: 'AUTOMATION_RULES',
  AUTOMATION_RULE_REVIEW: 'AUTOMATION_RULE_REVIEW',
  AUTOMATION_RULE_HISTORY: 'AUTOMATION_RULE_HISTORY',
  UI_SETTINGS: 'UI_SETTINGS',
  ROLE_FORM_WORKFLOW_MAPPING: 'ROLE_FORM_WORKFLOW_MAPPING',
  LEGAL_PAGES: 'LEGAL_PAGES',
  
  // Workspace (Operational roles)
  WORKSPACE: 'WORKSPACE',
  WORKSPACE_CASES: 'WORKSPACE_CASES',
  WORKSPACE_ORDERS: 'WORKSPACE_ORDERS',
  WORKSPACE_APPROVALS: 'WORKSPACE_APPROVALS',
  WORKSPACE_ADVERSE_EVENTS: 'WORKSPACE_ADVERSE_EVENTS',
  WORKSPACE_FOLLOWUPS: 'WORKSPACE_FOLLOWUPS',
  WORKSPACE_CLAIMS: 'WORKSPACE_CLAIMS',
  /** Controls the "My Work" tab/page only — separate from WORKSPACE_APPROVALS,
   * which gates the actual approve/reject actions. */
  MY_WORK: 'MY_WORK',
  
  // Specialized Workspaces
  PHARMA_WORKSPACE: 'PHARMA_WORKSPACE',
  HOSPITAL_WORKSPACE: 'HOSPITAL_WORKSPACE',
  HCP_WORKSPACE: 'HCP_WORKSPACE',
  PATIENT_WORKSPACE: 'PATIENT_WORKSPACE',
  
  // Operational Modules
  CREATE_ASSISTANCE_CASE: 'CREATE_ASSISTANCE_CASE',
  ASSISTANCE_CASES: 'ASSISTANCE_CASES',
  ASSISTANCE_CLAIMS: 'ASSISTANCE_CLAIMS',
  CALL_LOGS: 'CALL_LOGS',
  ADVERSE_EVENTS: 'ADVERSE_EVENTS',
  
  // Reports & Audit
  REPORTS: 'REPORTS',
  AUDIT_REPORT: 'AUDIT_REPORT',
} as const;

export type MenuKey = (typeof MENU_KEYS)[keyof typeof MENU_KEYS];

/** Configuration-only menu keys - restricted to Program Admin */
export const CONFIGURATION_MENU_KEYS = new Set([
  MENU_KEYS.ROLES_CONFIG,
  MENU_KEYS.USERS_CONFIG,
  MENU_KEYS.FORMS_CONFIG,
  MENU_KEYS.WORKFLOW_CONFIG,
  MENU_KEYS.ASSISTANCE_SETTINGS,
  MENU_KEYS.FINANCIAL_PLAN,
  MENU_KEYS.NOTIFICATIONS_CONFIG,
  MENU_KEYS.AUTOMATION_RULES,
  MENU_KEYS.AUTOMATION_RULE_REVIEW,
  MENU_KEYS.AUTOMATION_RULE_HISTORY,
  MENU_KEYS.UI_SETTINGS,
  MENU_KEYS.ROLE_FORM_WORKFLOW_MAPPING,
  MENU_KEYS.LEGAL_PAGES,
]);

/** Workspace menu keys - for operational roles only */
export const WORKSPACE_MENU_KEYS = new Set([
  MENU_KEYS.WORKSPACE,
  MENU_KEYS.WORKSPACE_CASES,
  MENU_KEYS.WORKSPACE_ORDERS,
  MENU_KEYS.WORKSPACE_APPROVALS,
  MENU_KEYS.WORKSPACE_ADVERSE_EVENTS,
  MENU_KEYS.WORKSPACE_FOLLOWUPS,
  MENU_KEYS.WORKSPACE_CLAIMS,
  MENU_KEYS.MY_WORK,
  MENU_KEYS.PHARMA_WORKSPACE,
  MENU_KEYS.HOSPITAL_WORKSPACE,
  MENU_KEYS.HCP_WORKSPACE,
  MENU_KEYS.PATIENT_WORKSPACE,
]);

export const MENU_KEY_LABELS: Record<MenuKey, string> = {
  PROGRAM_SELECTION: 'Program Selection',
  PROGRAM_DASHBOARD: 'Program Dashboard',
  MANAGEMENT_DASHBOARD: 'Management Dashboard',
  OPERATIONAL_DASHBOARD: 'Operational Dashboard',
  FINANCE_DASHBOARD: 'Finance Dashboard',
  HOSPITALS: 'Hospitals',
  HCPS: 'HCPs',
  PATIENTS: 'Patients',
  PATIENT_360: 'Patient 360',
  PATIENT_ACTIVITY: 'Patient Workspace Activity',
  ROLES_CONFIG: 'Roles',
  USERS_CONFIG: 'Users',
  FORMS_CONFIG: 'Forms',
  WORKFLOW_CONFIG: 'Workflow Defined',
  ASSISTANCE_SETTINGS: 'Assistance Settings',
  FINANCIAL_PLAN: 'Financial Plan',
  NOTIFICATIONS_CONFIG: 'Notifications',
  AUTOMATION_RULES: 'Automation Rules',
  AUTOMATION_RULE_REVIEW: 'Automation Rule Review',
  AUTOMATION_RULE_HISTORY: 'Automation Rule History',
  UI_SETTINGS: 'UI Settings',
  ROLE_FORM_WORKFLOW_MAPPING: 'Role-Form-Workflow Mapping',
  LEGAL_PAGES: 'Legal Pages',
  WORKSPACE: 'Workspace',
  WORKSPACE_CASES: 'Cases',
  WORKSPACE_ORDERS: 'Orders',
  WORKSPACE_APPROVALS: 'Approvals',
  WORKSPACE_ADVERSE_EVENTS: 'Adverse Events',
  WORKSPACE_FOLLOWUPS: 'Follow-ups',
  WORKSPACE_CLAIMS: 'Claims',
  MY_WORK: 'My Work',
  PHARMA_WORKSPACE: 'Pharma Workspace',
  HOSPITAL_WORKSPACE: 'Hospital Workspace',
  HCP_WORKSPACE: 'HCP Workspace',
  PATIENT_WORKSPACE: 'Patient Workspace',
  CREATE_ASSISTANCE_CASE: 'Create Enrollment Case',
  ASSISTANCE_CASES: 'Assistance Cases',
  ASSISTANCE_CLAIMS: 'Assistance Claims',
  CALL_LOGS: 'Call Logs',
  ADVERSE_EVENTS: 'Adverse Events',
  REPORTS: 'Reports & Analytics',
  AUDIT_REPORT: 'Audit Report',
};

// ─────────────────────────────────────────────────────────────
// Service
// ─────────────────────────────────────────────────────────────

export const programRoleMenuPermissionService = {
  /**
   * Get the current user's accessible menu keys in a program.
   * Called on every program context entry to build the sidebar.
   */
  async getMyMenuAccess(programId: string): Promise<ApiResponse<MyProgramMenuAccessDto>> {
    const res = await apiClient.get<ApiResponse<MyProgramMenuAccessDto>>(
      `/programs/${programId}/role-menu-permissions/my-access`
    );
    return res.data;
  },

  /**
   * Get all menu permissions for a specific role (admin use).
   */
  async getRoleMenuPermissions(
    programId: string,
    roleId: string
  ): Promise<ApiResponse<RoleMenuPermissionsDto>> {
    const res = await apiClient.get<ApiResponse<RoleMenuPermissionsDto>>(
      `/programs/${programId}/role-menu-permissions/${roleId}`
    );
    return res.data;
  },

  /**
   * Bulk-update menu permissions for a role.
   */
  async updateRoleMenuPermissions(
    programId: string,
    roleId: string,
    request: UpdateRoleMenuPermissionsRequest
  ): Promise<ApiResponse<RoleMenuPermissionsDto>> {
    const res = await apiClient.put<ApiResponse<RoleMenuPermissionsDto>>(
      `/programs/${programId}/role-menu-permissions/${roleId}`,
      request
    );
    return res.data;
  },

  /**
   * Auto-seed default permissions for a role based on its RoleCategory.
   */
  async initializeRoleMenuPermissions(
    programId: string,
    roleId: string,
    reset = false
  ): Promise<ApiResponse<RoleMenuPermissionsDto>> {
    const res = await apiClient.post<ApiResponse<RoleMenuPermissionsDto>>(
      `/programs/${programId}/role-menu-permissions/${roleId}/initialize`,
      { reset }
    );
    return res.data;
  },
};
